// Training Center → Recommendations tab.
//
// Replaces web/scripts/training-center.js's tcLoadRecommendations()
// and _tcRenderRecommendations(). Card list of pending recommendations
// with Apply / Dismiss buttons + an Analyze button that kicks off a
// new analysis run. Runs history appears below.

import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AnalyzeRun, OrgScope, Recommendation } from "../../api/training";
import { trainingApi } from "../../api/training";
import { AdminGrid, ErrorBlock, LoadingBlock, formatDateTime, formatRelative } from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function RecommendationsTab({ bearer, scope }: Props) {
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [runs, setRuns] = useState<AnalyzeRun[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [busyRec, setBusyRec] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      trainingApi
        .recommendations(bearer, { ...scope, status: "pending" })
        
        .catch(() => [] as Recommendation[]),
      trainingApi
        .recommendationRuns(bearer, scope)
        
        .catch(() => [] as AnalyzeRun[]),
    ])
      .then(([r, runRows]) => {
        if (cancelled) return;
        setRecs(r);
        setRuns(runRows);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  useEffect(() => {
    setLoading(true);
    const c = reload();
    setLoading(false);
    return c;
  }, [reload]);

  const startAnalyze = async () => {
    if (!scope.organization) {
      setError(new Error("Select an organization to analyze."));
      return;
    }
    setAnalyzing(true);
    setError(null);
    try {
      await trainingApi.analyze(bearer, scope);
    } catch (e) {
      setError(e as Error);
    } finally {
      // Poll for completion: refresh runs every 4s for up to 2 min.
      let attempts = 0;
      const tick = async () => {
        attempts++;
        const c = await trainingApi
          .recommendationRuns(bearer, scope)
          
          .catch(() => null);
        if (c) {
          setRuns(c);
          const latest = c[0];
          if (!latest || (latest.status !== "queued" && latest.status !== "in_progress")) {
            setAnalyzing(false);
            reload();
            return;
          }
        }
        if (attempts < 30) setTimeout(tick, 4000);
        else setAnalyzing(false);
      };
      setTimeout(tick, 4000);
    }
  };

  const handleApply = async (rec: Recommendation) => {
    setBusyRec(rec.id);
    try {
      await trainingApi.applyRecommendation(bearer, rec.id);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusyRec(null);
    }
  };

  const handleDismiss = async (rec: Recommendation) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt("Dismiss reason (optional):") ?? undefined;
    setBusyRec(rec.id);
    try {
      await trainingApi.dismissRecommendation(bearer, rec.id, reason);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusyRec(null);
    }
  };

  const runColDefs = useMemo<ColDef<AnalyzeRun>[]>(
    () => [
      {
        field: "created_at",
        headerName: "Started",
        flex: 1,
        sort: "desc",
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        cellRenderer: (p: { value: AnalyzeRun["status"] }) => <RunStatusBadge status={p.value} />,
      },
      {
        field: "recommendations_created",
        headerName: "Created",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => (p.value ?? "—") as string,
      },
      {
        field: "finished_at",
        headerName: "Finished",
        flex: 1,
        valueFormatter: (p) => (p.value ? formatRelative(p.value as string) : "—"),
      },
      {
        field: "error",
        headerName: "Error",
        flex: 2,
        autoHeight: true,
        wrapText: true,
        cellClass: "text-red-700 text-xs",
        valueFormatter: (p) => (p.value as string) || "",
      },
    ],
    [],
  );

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-900">AI Recommendations</h2>
        <button
          type="button"
          onClick={startAnalyze}
          disabled={analyzing}
          className="rounded-md bg-brand text-white px-3 h-9 text-sm font-medium hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {analyzing ? "Analyzing…" : "Analyze"}
        </button>
      </div>

      {error && <ErrorBlock error={error} />}

      {recs.length === 0 ? (
        <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
          Click "Analyze" to generate AI recommendations for improving query quality.
        </div>
      ) : (
        <div className="space-y-3">
          {recs.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              busy={busyRec === rec.id}
              onApply={() => handleApply(rec)}
              onDismiss={() => handleDismiss(rec)}
            />
          ))}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">Analysis Runs</h3>
        {runs.length === 0 ? (
          <div className="text-sm text-neutral-500">No runs yet.</div>
        ) : (
          <AdminGrid<AnalyzeRun> rowData={runs.slice(0, 15)} columnDefs={runColDefs} />
        )}
      </div>
    </div>
  );
}

function RecommendationCard({
  rec,
  busy,
  onApply,
  onDismiss,
}: {
  rec: Recommendation;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-[14px] border border-line bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-block rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium px-2 py-0.5">
              {rec.rec_type}
            </span>
            {rec.impact_score != null && (
              <span className="text-[11px] text-neutral-500">
                impact {rec.impact_score.toFixed(2)}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-semibold text-neutral-900">{rec.title}</div>
          {rec.description && (
            <div className="mt-1 text-sm text-neutral-700">{rec.description}</div>
          )}
          {rec.rationale && (
            <details className="mt-2 text-xs text-neutral-600">
              <summary className="cursor-pointer hover:text-neutral-900">Why?</summary>
              <div className="mt-1 whitespace-pre-wrap">{rec.rationale}</div>
            </details>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            type="button"
            onClick={onApply}
            disabled={busy}
            className="rounded-md bg-emerald-600 text-white px-3 h-8 text-xs font-medium hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="rounded-md border border-line text-neutral-800 px-3 h-8 text-xs font-medium hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function RunStatusBadge({ status }: { status: AnalyzeRun["status"] }) {
  const cls =
    status === "completed"
      ? "bg-emerald-50 text-emerald-700"
      : status === "in_progress" || status === "queued"
        ? "bg-blue-50 text-blue-700"
        : "bg-red-50 text-red-700";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {status}
    </span>
  );
}
